/**
 * Ezoko – "+ Kosárba" buttons in the add-on rows (sphere holders, sage / palo santo).
 * Adds to the cart the same way the theme's own Add to cart does (CartLinesUpdateEvent),
 * so the cart count, the cart drawer and the free-shipping bar update and the page stays.
 * Also: the free sphere holder box ([data-ezgift]) – when the theme's own Add to cart adds the sphere,
 * the chosen holder is added right after it (once per cart).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const addUrl = routes.cart_add_url || '/cart/add';
const cartUrl = routes.cart_url || '/cart';

function cartSections() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function refreshCart() {
  const items = document.querySelector('cart-items-component');
  if (items && typeof items.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return items.fetchCartData();
  }
  const r = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) throw new Error('cart ' + r.status);
  return r.json();
}

function setState(btn, state) {
  const label = btn.querySelector('[data-ezar-label]') || btn;
  if (!btn.dataset.t) btn.dataset.t = label.textContent;
  if (state === 'busy') {
    btn.disabled = true;
    btn.classList.remove('is-ok', 'is-err');
    btn.setAttribute('aria-busy', 'true');
  } else if (state === 'ok') {
    btn.removeAttribute('aria-busy');
    btn.classList.add('is-ok');
    label.textContent = '✓ ' + (btn.dataset.ok || '');
    setTimeout(() => {
      btn.disabled = false;
      btn.classList.remove('is-ok');
      label.textContent = btn.dataset.t;
    }, 2500);
  } else {
    btn.removeAttribute('aria-busy');
    btn.classList.add('is-err');
    label.textContent = btn.dataset.err || btn.dataset.t;
    setTimeout(() => {
      btn.disabled = false;
      btn.classList.remove('is-err');
      label.textContent = btn.dataset.t;
    }, 2500);
  }
}

/** Adds one variant the theme's way. Returns true when it worked. */
async function addVariant(id, el, productId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  el.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const body = new FormData();
  body.append('id', id);
  body.append('quantity', '1');
  const sections = cartSections();
  if (sections) body.append('sections', sections);

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body,
      credentials: 'same-origin',
    });
    const json = await res.json();
    const ajaxCart = await refreshCart();
    const failed = Boolean(json && json.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'ez-addon-row',
        sourceId: 'ezar-' + id,
        itemCount: 1,
        productId,
        sections: json && json.sections,
        didError: failed,
      },
    });
    return !failed;
  } catch (e) {
    deferred.reject(e);
    return false;
  }
}

async function add(btn) {
  const id = btn.dataset.id;
  if (!id) return;
  setState(btn, 'busy');
  const ok = await addVariant(id, btn, btn.dataset.product);
  setState(btn, ok ? 'ok' : 'err');
}

/** Free holder: after the theme adds the sphere, add the chosen holder (if no holder from the list is in the cart yet). */
function onThemeAdd(e) {
  if (e.action !== 'add' || !e.promise) return;
  const form = e.target instanceof Element ? e.target.closest('product-form-component') : null;
  if (!form) return;
  const box = document.querySelector(`[data-ezgift][data-for="${form.dataset.productId}"]`);
  if (!box) return;
  const pick = box.querySelector('input[type="radio"]:checked');
  if (!pick) return;
  const giftIds = (box.dataset.all || '').split(',').filter(Boolean);

  e.promise
    .then(async (result) => {
      if (result && result.detail && result.detail.didError) return;
      const cart = await refreshCart();
      const has = (cart.items || []).some((i) => giftIds.includes(String(i.variant_id)));
      if (has) return;
      await addVariant(pick.value, box, pick.dataset.product);
    })
    .catch(() => {});
}

if (!window.__ezarReady) {
  window.__ezarReady = true;

  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-ezar-add]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    add(btn);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, onThemeAdd);

  document.addEventListener('change', (e) => {
    const sel = e.target instanceof Element ? e.target.closest('[data-ezar-v]') : null;
    if (!sel) return;
    const btn = sel.parentElement && sel.parentElement.querySelector('[data-ezar-add]');
    if (btn) btn.dataset.id = sel.value;
  });
}